/**
 * Convert a wheel event's delta into a multiplicative zoom factor.
 *
 * macOS trackpad pinch gestures arrive as `wheel` events (with `ctrlKey`)
 * carrying SMALL pixel-mode `deltaY` magnitudes (~1-10). Mouse-wheel notches
 * arrive with much larger deltas (~±100) — often in line mode. To make the
 * pinch feel responsive without a fast mouse flick overshooting, we:
 *
 *  1. Normalize `deltaMode` (lines / pages) to pixels so both input kinds are
 *     scaled on the same footing.
 *  2. Apply a sensitivity coefficient (~5x the historical 0.001).
 *  3. Clamp the per-event factor to a sane range so a single fast flick can't
 *     overshoot wildly.
 */

/** Sensitivity coefficient applied to the pixel-normalized delta. */
export const WHEEL_ZOOM_COEFF = 0.005;

/** Pixels-per-line assumed when a wheel event reports line deltaMode. */
export const WHEEL_LINE_HEIGHT_PX = 16;

/** Per-event clamp on the zoom factor to prevent overshoot on a fast flick. */
export const WHEEL_ZOOM_MIN_FACTOR = 0.5;
export const WHEEL_ZOOM_MAX_FACTOR = 2.0;

/**
 * @param deltaY         The wheel event's `deltaY`.
 * @param deltaMode      The wheel event's `deltaMode` (0=pixel, 1=line, 2=page).
 * @param viewportHeight Height (px) used to normalize page-mode deltas.
 * @returns A multiplicative zoom factor (>1 zooms in, <1 zooms out).
 */
export function wheelZoomFactor(
  deltaY: number,
  deltaMode: number,
  viewportHeight: number,
): number {
  const unit =
    deltaMode === 1
      ? WHEEL_LINE_HEIGHT_PX
      : deltaMode === 2
        ? viewportHeight
        : 1;
  const factor = Math.exp(-deltaY * unit * WHEEL_ZOOM_COEFF);
  return Math.min(
    WHEEL_ZOOM_MAX_FACTOR,
    Math.max(WHEEL_ZOOM_MIN_FACTOR, factor),
  );
}
